import React, { useEffect, useMemo, useRef, useState } from 'react';
import './ArchitecturalHud.css';

const SPINNER_MESSAGES = [
  'HASHING BEHAVIORAL FINGERPRINT',
  'VALIDATING INPUT INTEGRITY',
  'SCANNING RESPONSE AUTHENTICITY',
  'CROSS-REFERENCING KEYSTROKE PATTERNS',
  'EVALUATING PASTE-TO-INPUT RATIO',
  'CLASSIFYING ANSWER ORIGINALITY',
];

// --- Real security scan line generators ---
function buildSecurityScanLines(telemetry) {
  if (!telemetry || typeof telemetry !== 'object') return [];
  const lines = [];
  const ts = () => new Date().toTimeString().slice(0, 8);

  // Timing analysis
  const sec = telemetry.totalSeconds || 0;
  if (sec > 0) {
    lines.push(`[${ts()}] [TIMING] Assessment duration: ${sec}s`);
    if (sec < 120) lines.push(`[${ts()}] [TIMING] \u26a0 FLAGGED \u2014 completion speed below threshold`);
    else if (sec < 180) lines.push(`[${ts()}] [TIMING] Marginal \u2014 monitoring for pattern`);
    else lines.push(`[${ts()}] [TIMING] Duration within expected range`);
  }

  // Paste analysis
  const pastes = telemetry.totalPastes || 0;
  const keystrokes = telemetry.totalKeystrokes || 0;
  const pastedOnly = telemetry.pastedOnlyCount || 0;
  lines.push(`[${ts()}] [INPUT] Total keystrokes recorded: ${keystrokes}`);
  lines.push(`[${ts()}] [INPUT] Paste events detected: ${pastes}`);
  if (pastedOnly > 0) {
    lines.push(`[${ts()}] [INPUT] \u26a0 ${pastedOnly} answer(s) appear paste-only (minimal typing)`);
  }
  if (pastes > 0 && keystrokes > 0) {
    const ratio = ((pastes / (pastes + keystrokes)) * 100).toFixed(1);
    lines.push(`[${ts()}] [INPUT] Paste-to-input ratio: ${ratio}%`);
  }

  // Per-answer keystroke scan
  const kpa = telemetry.keystrokesPerAnswer || [];
  const ppa = telemetry.pastesPerAnswer || [];
  for (let i = 0; i < Math.max(kpa.length, ppa.length); i++) {
    const k = kpa[i] || 0;
    const p = ppa[i] || 0;
    const flag = (p > 0 && k < 5) ? ' \u26a0 PASTE-ONLY' : '';
    lines.push(`[${ts()}] [SCAN] Answer ${i + 1}: ${k} keystrokes, ${p} paste(s)${flag}`);
  }

  // First keystroke delays
  const fkDeltas = telemetry.firstKeystrokeDeltas || [];
  const validDeltas = fkDeltas.filter(d => d !== null);
  if (validDeltas.length > 0) {
    const avg = (validDeltas.reduce((a, b) => a + b, 0) / validDeltas.length).toFixed(1);
    lines.push(`[${ts()}] [TIMING] Avg time-to-first-keystroke: ${avg}s`);
  }

  return lines;
}

// Build lines that echo actual answer text with inspection framing
function buildAnswerEchoLines(answers) {
  if (!Array.isArray(answers) || answers.length === 0) return [];
  const lines = [];
  const ts = () => new Date().toTimeString().slice(0, 8);
  const INSPECTION_TAGS = [
    'ORIGINALITY',
    'AUTHENTICITY',
    'PATTERN_MATCH',
    'COHERENCE',
    'DEPTH_ANALYSIS',
    'STRUCTURE',
  ];
  for (let i = 0; i < answers.length; i++) {
    const text = (answers[i] || '').trim();
    if (!text) continue;
    const tag = INSPECTION_TAGS[i % INSPECTION_TAGS.length];
    // Truncate to ~80 chars for the echo, add ellipsis if longer
    const snippet = text.length > 80 ? text.slice(0, 77) + '...' : text;
    lines.push(`[${ts()}] [${tag}] Inspecting answer ${i + 1}...`);
    lines.push(`[${ts()}] [ECHO] "${snippet}"`);
    // Add a scan result line
    const wordCount = text.split(/\s+/).filter(Boolean).length;
    const charDiv = new Set(text.toLowerCase().replace(/\s/g, '')).size;
    lines.push(`[${ts()}] [${tag}] ${wordCount} words | ${charDiv} unique chars | classifying...`);
  }
  return lines;
}

const SECURITY_AMBIENT_LINES = [
  '[INTEGRITY] Behavioral fingerprint hashing...',
  '[INTEGRITY] Session token verified',
  '[SCAN] Input quality gate: active',
  '[SCAN] Prompt injection filter: armed',
  '[VERIFY] Cross-referencing keystroke patterns',
  '[VERIFY] Answer authenticity scoring in progress',
  '[INTEGRITY] Rate limit check: nominal',
  '[SCAN] Character diversity analysis: running',
  '[VERIFY] Response originality vector: computing',
  '[INTEGRITY] Daily assessment quota: checked',
  '[SCAN] Behavioral anomaly detector: online',
  '[VERIFY] Input pattern classifier: active',
];

function makePulseLine() {
  const now = new Date();
  const stamp = now.toTimeString().slice(0, 8);
  const msg = SPINNER_MESSAGES[Math.floor(Math.random() * SPINNER_MESSAGES.length)];
  return `[${stamp}] [INTEGRITY] ${msg}`;
}

export default function ArchitecturalHud({
  mode,
  exchangeCount = 0,
  demoComplete = false,
  lessonCount = 0,
  assessedLevel = null,
  limitReached = false,
  processing = false,
  title = 'Live Data',
  studentId = '',
  sandboxRevealActive = false,
  audioLevel = 0,
  instructorActive = false,
  securityTelemetry = null,
}) {
  const [logs, setLogs] = useState(() => [
    '[INTEGRITY] SESSION INITIALIZED. MONITORING ACTIVE',
    '[SCAN] INPUT VALIDATION PIPELINE ONLINE',
  ]);
  const securityScanLines = useMemo(() => buildSecurityScanLines(securityTelemetry), [securityTelemetry]);
  const answerEchoLines = useMemo(() => buildAnswerEchoLines(securityTelemetry?.answerSnippets), [securityTelemetry]);
  const hasSecurity = securityScanLines.length > 0;

  const [activityLines, setActivityLines] = useState(() => [
    '[INTEGRITY] Behavioral analysis engine loaded',
    '[SCAN] Injection filter armed',
    '[VERIFY] Awaiting submission data',
  ]);
  const [activityScrollPct, setActivityScrollPct] = useState(100);

  const prevExchange = useRef(exchangeCount);
  const prevLesson = useRef(lessonCount);
  const prevDemoComplete = useRef(demoComplete);
  const prevLimitReached = useRef(limitReached);
  const activityRef = useRef(null);

  const progress = useMemo(() => {
    if (mode === 'demo') {
      return Math.max(0, Math.min(1, exchangeCount / 6));
    }
    if (mode === 'training') {
      return limitReached ? 1 : Math.max(0, Math.min(1, lessonCount / 4));
    }
    return 0;
  }, [mode, exchangeCount, lessonCount, limitReached]);

  useEffect(() => {
    const interval = setInterval(() => {
      setLogs((prev) => [...prev.slice(-8), makePulseLine()]);
    }, 3600);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (mode === 'demo' && exchangeCount > prevExchange.current) {
      setLogs((prev) => [...prev.slice(-8), `[VERIFY] EXCHANGE ${exchangeCount}/6 CAPTURED — ANALYZING`]);
    }
    prevExchange.current = exchangeCount;
  }, [mode, exchangeCount]);

  useEffect(() => {
    if (mode === 'training' && lessonCount > prevLesson.current) {
      setLogs((prev) => [...prev.slice(-8), `[VERIFY] LESSON ${lessonCount} PROGRESS LOGGED`]);
    }
    prevLesson.current = lessonCount;
  }, [mode, lessonCount]);

  useEffect(() => {
    if (demoComplete && !prevDemoComplete.current) {
      setLogs((prev) => [...prev.slice(-8), '[INTEGRITY] DIAGNOSTIC COMPLETE. FINAL SCAN RUNNING']);
    }
    prevDemoComplete.current = demoComplete;
  }, [demoComplete]);

  useEffect(() => {
    if (limitReached && !prevLimitReached.current) {
      setLogs((prev) => [...prev.slice(-8), '[VERIFY] BLOCK COMPLETE. INTEGRITY CHECK PASSED']);
    }
    prevLimitReached.current = limitReached;
  }, [limitReached]);

  useEffect(() => {
    if (assessedLevel) {
      setLogs((prev) => [...prev.slice(-8), `[ASSESSMENT] LEVEL RESOLVED: ${assessedLevel.toUpperCase()}`]);
    }
  }, [assessedLevel]);

  const modeLabel = mode === 'training' ? 'Training' : mode === 'demo' ? 'Diagnostic' : 'Discovery';
  const studentLabel = studentId || 'Guest session';
  const showCompactPulse = mode === 'discovery' || mode === 'demo';
  const pulseLabel = processing
    ? 'Authenticity analysis in progress'
    : sandboxRevealActive
      ? 'Final integrity scan running'
      : 'Monitoring active';
  const clampedAudioLevel = Math.max(0, Math.min(1, Number(audioLevel) || 0));
  const vuLevel = instructorActive ? clampedAudioLevel : 0;

  const codeReveal = useMemo(() => {
    const idleRows = [
      '> integrity.watch(input.events)',
      '> scan.validate(session.state)',
      '> verify.depth(behavioral=true)',
      '> monitor.tick(interval=250ms)',
    ];
    const activeRows = [
      '> integrity.finalize(submission)',
      '> scan.classify(answers, origin)',
      '> verify.authenticity(keystroke)',
      '> report.compile(diagnostic)',
    ];
    const rows = (sandboxRevealActive || processing) ? activeRows : idleRows;
    return rows;
  }, [sandboxRevealActive, processing]);

  // Inject real security scan lines once when telemetry arrives
  const securityInjected = useRef(false);
  useEffect(() => {
    if (hasSecurity && !securityInjected.current) {
      securityInjected.current = true;
      // Phase 1: Drip behavioral scan lines
      securityScanLines.forEach((line, i) => {
        setTimeout(() => {
          setActivityLines((prev) => [...prev.slice(-120), line]);
        }, 600 + i * 400);
      });
      // Phase 2: After scan lines, drip answer echoes (their actual text)
      const echoStart = 600 + securityScanLines.length * 400 + 800;
      answerEchoLines.forEach((line, i) => {
        setTimeout(() => {
          setActivityLines((prev) => [...prev.slice(-120), line]);
        }, echoStart + i * 500);
      });
    }
  }, [hasSecurity, securityScanLines, answerEchoLines]);

  useEffect(() => {
    const streamInterval = setInterval(() => {
      const stamp = new Date().toTimeString().slice(0, 8);
      const activeRows = [
        '[VERIFY] Compiling authenticity report',
        '[SCAN] Classifying answer origin vectors',
        '[INTEGRITY] Finalizing behavioral signature',
        '[VERIFY] Generating diagnostic confidence score',
      ];
      const idleRows = hasSecurity ? SECURITY_AMBIENT_LINES : [
        '[INTEGRITY] Session heartbeat accepted',
        '[SCAN] Input quality gate: stable',
        '[VERIFY] Behavioral index: warmed',
        '[INTEGRITY] Awaiting submission event',
      ];
      const rowPool = (processing || sandboxRevealActive) ? activeRows : idleRows;
      const row = rowPool[Math.floor(Math.random() * rowPool.length)];
      setActivityLines((prev) => [...prev.slice(-120), `[${stamp}] ${row}`]);
    }, processing || sandboxRevealActive ? 900 : 1800);
    return () => clearInterval(streamInterval);
  }, [processing, sandboxRevealActive, hasSecurity]);

  useEffect(() => {
    const box = activityRef.current;
    if (!box) return;
    if (activityScrollPct >= 99) {
      box.scrollTop = box.scrollHeight;
    }
  }, [activityLines, activityScrollPct]);

  function syncScrollPct() {
    const box = activityRef.current;
    if (!box) return;
    const maxTop = Math.max(1, box.scrollHeight - box.clientHeight);
    const nextPct = Math.round((box.scrollTop / maxTop) * 100);
    setActivityScrollPct(nextPct);
  }

  function handleSliderChange(event) {
    const nextPct = Number(event.target.value);
    setActivityScrollPct(nextPct);
    const box = activityRef.current;
    if (!box) return;
    const maxTop = Math.max(0, box.scrollHeight - box.clientHeight);
    box.scrollTop = (nextPct / 100) * maxTop;
  }

  return (
    <aside className={`ahud ${showCompactPulse ? 'is-compact' : ''}`} style={{ '--ahud-progress': progress }} aria-hidden="true">
      {showCompactPulse ? (
        <section className="ahud-card ahud-card-pulse">
          <div className={`ahud-vu-rail ${instructorActive ? 'is-active' : ''}`}>
            <div className="ahud-vu-fill" style={{ height: `${Math.max(8, vuLevel * 100)}%` }} />
          </div>
          <div className="ahud-pulse-row">
            <span className={`ahud-pulse-dot ${processing ? 'is-hot' : ''}`} />
            <div>
              <h3>{title}</h3>
              <p className="ahud-pulse-label">{pulseLabel}</p>
            </div>
          </div>
          <div className={`ahud-code-reveal ${(sandboxRevealActive || processing) ? 'is-active' : 'is-idle'}`}>
            <div
              ref={activityRef}
              className="ahud-code-track"
              onScroll={syncScrollPct}
            >
              {codeReveal.map((line) => (
                <div key={`preset-${line}`} className="ahud-line ahud-line-preset">{line}</div>
              ))}
              {activityLines.map((line, index) => (
                <div key={`activity-${index}-${line}`} className="ahud-line">{line}</div>
              ))}
            </div>
            <input
              type="range"
              min="0"
              max="100"
              step="1"
              value={activityScrollPct}
              onChange={handleSliderChange}
              className="ahud-scroll-slider"
              aria-label="Scroll background activity"
            />
          </div>
        </section>
      ) : (
        <section className="ahud-card">
          <div className="ahud-head">
            <h3>{title}</h3>
            <span className={`ahud-state ${processing ? 'is-hot' : ''}`}>{processing ? 'Scanning' : 'Monitoring'}</span>
          </div>
          <ul className="ahud-log">
            {logs.slice(-9).map((line, index) => (
              <li key={`${line}-${index}`}>{line}</li>
            ))}
          </ul>
        </section>
      )}

      <section className="ahud-card ahud-progress">
        <h3>Student Progress</h3>
        <div className="ahud-progress-track">
          <div className="ahud-progress-chaos" />
          <div className="ahud-progress-solid" />
        </div>
        <p>{modeLabel} Verification: {(progress * 100).toFixed(0)}%</p>
        <p className="ahud-student-id">Session: {studentLabel}</p>
        <div className="ahud-mentor">
          <div className="ahud-orb" />
          <span>Integrity engine synchronized. All inputs monitored.</span>
        </div>
      </section>
    </aside>
  );
}
